/**
 * Which discount is being entered, and the one place the two shapes exclude
 * each other.
 *
 * A discount is a KIND plus a VALUE. The wire model still carries
 * `discount_percent` and `discount_amount` side by side, and the server refuses
 * a configuration with both set (`two_discounts`: "two discounts on one
 * agreement is ambiguous about which applies first"). The screen therefore must
 * not be able to produce that state — not because the server would miss it, but
 * because offering an operator a way to enter something that is always rejected
 * is a trap with a validation message on the end of it.
 *
 * The configurator previously exposed the percentage only and zeroed the amount
 * on every keystroke, so the flat shape the commercial model supports was
 * unreachable from the Admin UI entirely.
 *
 * NO ARITHMETIC HERE. This decides which field an operator is editing; what the
 * discount comes to is the server's answer, from `commercial_math`.
 */

export const PERCENT = 'percentage';
export const FLAT = 'flat';

export const DISCOUNT_KINDS = Object.freeze([
  { kind: PERCENT, label: 'Percent', suffix: '%' },
  { kind: FLAT, label: 'Amount', suffix: '' },
]);

/**
 * The shape currently in play, read from the config rather than held twice.
 *
 * Deriving it means the control cannot disagree with what will be sent — a
 * separate `useState` for the kind is how a screen comes to show "Amount" while
 * posting a percentage.
 */
export const activeDiscount = (config) => {
  const percent = Number(config?.discount_percent) || 0;
  const amount = Number(config?.discount_amount) || 0;
  // Percent wins when both are somehow set, matching the server's own
  // documented tie-break, so the UI never disagrees with the total it renders.
  if (percent) return { kind: PERCENT, value: percent };
  if (amount) return { kind: FLAT, value: amount };
  return { kind: PERCENT, value: 0 };
};

/**
 * THE MUTUAL EXCLUSION, in one place. Every write to a discount field goes
 * through this, so the other shape is always cleared in the same operation.
 */
export const discountPatch = (kind, value) => {
  const n = Number(value);
  const safe = Number.isFinite(n) && n > 0 ? n : 0;
  if (kind === FLAT) return { discount_amount: safe, discount_percent: 0 };
  return { discount_percent: safe, discount_amount: 0 };
};

/**
 * What the operator is told about a discount that has nothing to apply to.
 *
 * The estate currently prices nothing — every catalogue entry carries a null
 * price — so a discount entered against an unpriced basket produces no visible
 * change at all, and the field looks broken rather than inapplicable. Stating
 * it is the same rule as "Not priced" on the Licences tab: unknown and zero are
 * different facts.
 */
export const discountAppliesTo = (totals) => {
  if (!totals) return null;
  if (totals.priced === false) return 'This basket has no priced lines, so a discount changes nothing.';
  if ((totals.unpriced_lines || []).length > 0) {
    return 'Some lines have no price, so the discount applies only to the priced ones.';
  }
  if (!Number(totals.subtotal)) return 'The subtotal is zero, so a discount changes nothing.';
  return null;
};
